import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Spinner } from '@/components/atoms/Spinner';
import { Button } from '@/components/atoms/Button';
import { WizardLayout } from '@/features/intake/WizardLayout';
import { Step1Basics } from '@/features/intake/steps/Step1Basics';
import { Step2Scale } from '@/features/intake/steps/Step2Scale';
import { Step3Financials } from '@/features/intake/steps/Step3Financials';
import { Step4Timeline } from '@/features/intake/steps/Step4Timeline';
import { Step5Equity } from '@/features/intake/steps/Step5Equity';
import { Step6CommunityImpact } from '@/features/intake/steps/Step6CommunityImpact';
import { CommunityTagsStep } from '@/features/community-tags/CommunityTagsStep';
import { STEP_LABELS, STEP_ORDER, type StepName } from '@/features/intake/schemas';
import { canNavigateToStep, maxReachableStepIndex } from '@/features/intake/stepGuard';
import { useProject, useSubmitIntake } from '@/lib/api/projectApi';

export const Route = createFileRoute('/projects/$projectId/intake/$step')({
  component: IntakeStepPage,
});

function IntakeStepPage() {
  const { projectId, step } = Route.useParams();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const submit = useSubmitIntake(projectId);

  if (!STEP_ORDER.includes(step as StepName)) {
    return <Navigate to="/projects/$projectId/intake/$step" params={{ projectId, step: 'basics' }} replace />;
  }
  const stepName = step as StepName;

  if (project.isLoading || !project.data) {
    return <div className="flex min-h-screen items-center justify-center"><Spinner aria-label="Loading project" /></div>;
  }

  // T206 — guard: redirect to last reachable step if user tries to jump ahead.
  if (!canNavigateToStep(stepName, project.data)) {
    const allowed = STEP_ORDER[maxReachableStepIndex(project.data)];
    return <Navigate to="/projects/$projectId/intake/$step" params={{ projectId, step: allowed }} replace />;
  }

  const onSaveAndExit = () => navigate({ to: '/' });
  const isLast = stepName === 'community-impact';

  // A 422 from submit means required fields are still missing. Surface them (grouped
  // by section, each a link back to that step) instead of failing silently.
  const submitErrors = submit.error?.response?.data.errors;
  const footerAlert = submitErrors && submitErrors.length > 0
    ? (
      <div role="alert" className="rounded-md border border-gap-border bg-gap-bg px-4 py-3 text-body-sm text-foreground">
        <p className="font-medium">Complete these required fields before you can submit:</p>
        <ul className="mt-1.5 space-y-1">
          {submitErrors.map((err) => {
            const sectionStep = err.section as StepName;
            const label = STEP_LABELS[sectionStep] ?? err.section;
            return (
              <li key={`${err.section}.${err.field}`}>
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-primary"
                  onClick={() =>
                    navigate({
                      to: '/projects/$projectId/intake/$step',
                      params: { projectId, step: sectionStep },
                    })
                  }
                >
                  {label}
                </button>
                {' — '}
                {err.message}
              </li>
            );
          })}
        </ul>
      </div>
    )
    : undefined;

  const primaryAction = isLast
    ? (
      <Button
        variant="primary"
        loading={submit.isPending}
        onClick={async () => {
          try {
            await submit.mutateAsync();
            // T225 — land on the workspace overview tab after submit.
            navigate({
              to: '/projects/$projectId/workspace/$tab',
              params: { projectId, tab: 'overview' },
            });
          } catch {
            // Server-side 422 leaves us on the page; errors surface via Topbar SaveStatus.
          }
        }}
      >
        Submit intake →
      </Button>
    )
    : undefined;

  return (
    <WizardLayout
      projectName={project.data.name}
      currentStep={stepName}
      onSaveAndExit={onSaveAndExit}
      primaryAction={primaryAction}
      footerAlert={footerAlert}
    >
      {stepName === 'basics' && <Step1Basics projectId={projectId} initial={project.data.basics} />}
      {stepName === 'scale' && <Step2Scale projectId={projectId} initial={project.data.scale} />}
      {stepName === 'financials' && <Step3Financials projectId={projectId} initial={project.data.financials} />}
      {stepName === 'timeline' && <Step4Timeline projectId={projectId} initial={project.data.timeline} />}
      {stepName === 'equity' && <Step5Equity projectId={projectId} initial={project.data.equity} />}
      {stepName === 'community-tags' && <CommunityTagsStep projectId={projectId} />}
      {stepName === 'community-impact' && <Step6CommunityImpact projectId={projectId} />}
    </WizardLayout>
  );
}
